import BreadCrumbsHeader from '@/components/BreadCrumbsHeader';
import { PageLayout } from '@/components/PageLayout';
import {
  SanitizeReviewLesson,
  SanitizeReviewRun,
} from '@/models/sanitizeReview';
import {
  useSanitizeReviewContent,
  useSanitizeReviewLessons,
  useSanitizeReviewRuns,
} from '@/query/sanitizeReview';
import { Button, Spinner, Tag } from '@teo/components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LessonComparison, { SanitizeReviewViewMode } from './LessonComparison';
import {
  getSanitizeReviewRiskLevel,
  getSanitizeReviewRiskScore,
  sortSanitizeReviewLessonsByRisk,
} from './sanitizeReviewRisk';

const LESSON_PAGE_SIZE = 20;

function formatDate(value: string | null | undefined, locale: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatBytes(value: number | string | null | undefined) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizedStatus(status: string) {
  return String(status || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_');
}

function readableStatus(status: string) {
  return String(status || 'unknown')
    .replace(/_/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());
}

function runTagVariant(status: string) {
  const normalized = normalizedStatus(status);
  if (normalized === 'completed') return 'success' as const;
  if (normalized === 'completed_with_errors') return 'warning' as const;
  if (['failed', 'cancelled', 'abandoned', 'expired'].includes(normalized)) {
    return 'error' as const;
  }
  return 'primary' as const;
}

function lessonTagVariant(lesson: SanitizeReviewLesson) {
  if (normalizedStatus(lesson.status) === 'failed') return 'error' as const;
  if (lesson.changed === true) return 'warning' as const;
  if (lesson.changed === false) return 'success' as const;
  return 'primary' as const;
}

function riskTagVariant(lesson: SanitizeReviewLesson) {
  const level = getSanitizeReviewRiskLevel(lesson);
  if (level === 'critical') return 'error' as const;
  if (level === 'high') return 'warning' as const;
  if (level === 'medium') return 'primary' as const;
  if (level === 'none') return 'success' as const;
  return 'default' as const;
}

function RiskScoreTag({ lesson }: { lesson: SanitizeReviewLesson }) {
  const { t } = useTranslation();
  const score = getSanitizeReviewRiskScore(lesson);
  const level = getSanitizeReviewRiskLevel(lesson);
  const title =
    score === null || level === null
      ? t('pages.sanitize_review.risk_not_analyzed')
      : t('pages.sanitize_review.risk_score', {
          level: t(`pages.sanitize_review.risk_levels.${level}`),
          score,
        });

  return (
    <Tag
      className="shrink-0"
      size="sm"
      title={title}
      variant={riskTagVariant(lesson)}
    />
  );
}

function getLessonName(
  lesson: SanitizeReviewLesson,
  t: ReturnType<typeof useTranslation>['t']
) {
  return (
    lesson.sourceLessonName ||
    t('pages.sanitize_review.lesson_number', {
      id: lesson.sourceLessonId,
    })
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex min-h-[12rem] items-center justify-center gap-3 rounded-lg border border-grey-02 bg-white p-8 text-sm text-grey-06">
      <Spinner ariaLabel={label} className="h-6 w-6" />
      <span>{label}</span>
    </div>
  );
}

function ErrorState({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="border-red-02 bg-red-01 flex min-h-[12rem] flex-col items-center justify-center gap-4 rounded-lg border p-8 text-center text-sm text-grey-09">
      <p>{label}</p>
      <Button onClick={onRetry} size="sm" variant="outline">
        {t('pages.sanitize_review.retry')}
      </Button>
    </div>
  );
}

function RunSummary({ run }: { run: SanitizeReviewRun }) {
  const { i18n, t } = useTranslation();

  return (
    <div className="rounded-lg border border-grey-02 bg-grey-01 p-3 text-xs text-grey-07">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Tag
          size="sm"
          title={t(
            `pages.sanitize_review.statuses.${normalizedStatus(run.status)}`,
            { defaultValue: readableStatus(run.status) }
          )}
          variant={runTagVariant(run.status)}
        />
        <time dateTime={run.createdAt}>
          {formatDate(run.createdAt, i18n.language)}
        </time>
      </div>
      <p>
        {t('pages.sanitize_review.run_counts', {
          changed: run.changedCount,
          failed: run.failedCount,
          processed: run.processedCount,
          total: run.totalCount,
        })}
      </p>
    </div>
  );
}

interface LessonListItemProps {
  lesson: SanitizeReviewLesson;
  onSelect: () => void;
  selected: boolean;
}

function LessonListItem({ lesson, onSelect, selected }: LessonListItemProps) {
  const { t } = useTranslation();
  const status = t(
    `pages.sanitize_review.statuses.${normalizedStatus(lesson.status)}`,
    { defaultValue: readableStatus(lesson.status) }
  );
  const result =
    lesson.changed === true
      ? t('pages.sanitize_review.changed')
      : lesson.changed === false
      ? t('pages.sanitize_review.unchanged')
      : status;

  return (
    <li>
      <button
        aria-current={selected ? 'true' : undefined}
        className={`w-full rounded-lg border p-3 text-left transition-colors ${
          selected
            ? 'border-primary-04 bg-primary-01'
            : 'border-grey-02 bg-white hover:border-grey-04 hover:bg-grey-01'
        }`}
        onClick={onSelect}
        type="button"
      >
        <span className="mb-2 flex items-start justify-between gap-2">
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-grey-09">
              {getLessonName(lesson, t)}
            </span>
            <span className="block text-xs text-grey-05">
              #{lesson.sourceLessonId}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-1">
            <RiskScoreTag lesson={lesson} />
            <Tag size="sm" title={result} variant={lessonTagVariant(lesson)} />
          </span>
        </span>
        <span className="block text-xs text-grey-06">
          {t('pages.sanitize_review.lesson_sizes', {
            sanitized: formatBytes(lesson.sanitizedSize),
            source: formatBytes(lesson.sourceSize),
          })}
        </span>
        {lesson.error?.code && (
          <span
            className="font-mono mt-1 block truncate text-xs text-error-06"
            title={lesson.error.message || undefined}
          >
            {lesson.error.code}
            {lesson.error.debug?.stage ? ` · ${lesson.error.debug.stage}` : ''}
          </span>
        )}
      </button>
    </li>
  );
}

function SanitizeReviewPage() {
  const { i18n, t } = useTranslation();
  const { lang = i18n.language, runId } = useParams<{
    lang: string;
    runId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changedFilter, setChangedFilter] = useState('all');
  const [lessonStatus, setLessonStatus] = useState('completed');
  const [offset, setOffset] = useState(0);
  const [viewMode, setViewMode] = useState<SanitizeReviewViewMode>('preview');

  const selectedLessonId = searchParams.get('lesson');
  const runsQuery = useSanitizeReviewRuns({ limit: 100, offset: 0 });
  const runs = useMemo(
    () => runsQuery.data?.rows || [],
    [runsQuery.data?.rows]
  );
  const selectedRun = runs.find((run) => run.id === runId);

  const lessonFilters = useMemo(
    () => ({
      changed:
        changedFilter === 'changed'
          ? true
          : changedFilter === 'unchanged'
          ? false
          : undefined,
      limit: LESSON_PAGE_SIZE,
      offset,
      order: 'risk' as const,
      status: lessonStatus || undefined,
    }),
    [changedFilter, lessonStatus, offset]
  );
  const lessonsQuery = useSanitizeReviewLessons(runId, lessonFilters);
  const lessons = useMemo(
    () => sortSanitizeReviewLessonsByRisk(lessonsQuery.data?.rows || []),
    [lessonsQuery.data?.rows]
  );
  const selectedLesson = lessons.find(
    (lesson) => lesson.id === selectedLessonId
  );
  const canLoadSelectedLesson = selectedLesson?.status === 'completed';
  const contentQuery = useSanitizeReviewContent(
    runId,
    selectedLessonId,
    canLoadSelectedLesson
  );

  const setSelectedLesson = (lessonId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (lessonId) next.set('lesson', lessonId);
    else next.delete('lesson');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (runId || !runs.length) return;
    const defaultRun =
      runs.find((run) => run.status === 'completed') || runs[0];
    navigate(`/${lang}/admin/sanitize-review/${defaultRun.id}`, {
      replace: true,
    });
  }, [lang, navigate, runId, runs]);

  useEffect(() => {
    if (lessonsQuery.isPreviousData) return;
    if (!lessons.length) {
      if (selectedLessonId) setSelectedLesson(null);
      return;
    }
    if (!selectedLessonId || !selectedLesson) {
      setSelectedLesson(lessons[0].id);
    }
    // Search params are intentionally omitted: they are read and written by
    // this effect, and including the object would retrigger it unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessons, lessonsQuery.isPreviousData, selectedLesson, selectedLessonId]);

  const changeRun = (nextRunId: string) => {
    setOffset(0);
    setLessonStatus('completed');
    setChangedFilter('all');
    navigate(
      nextRunId
        ? `/${lang}/admin/sanitize-review/${nextRunId}`
        : `/${lang}/admin/sanitize-review`
    );
  };

  const changeLessonFilter = (
    nextChangedFilter: string,
    nextStatus: string
  ) => {
    setOffset(0);
    setSelectedLesson(null);
    setChangedFilter(nextChangedFilter);
    setLessonStatus(nextStatus);
  };

  const totalLessons = lessonsQuery.data?.count || 0;
  const page = Math.floor(offset / LESSON_PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(totalLessons / LESSON_PAGE_SIZE));
  const lessonLabel = selectedLesson
    ? getLessonName(selectedLesson, t)
    : t('pages.sanitize_review.lesson');

  return (
    <PageLayout>
      <PageLayout.Content className="mx-auto min-h-[calc(100vh_-_150px)] w-full max-w-[1800px]">
        <BreadCrumbsHeader />

        <header className="mb-6 mt-6">
          <h1 className="text-2xl font-semibold text-grey-09">
            {t('pages.sanitize_review.title')}
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-grey-06">
            {t('pages.sanitize_review.description')}
          </p>
        </header>

        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
          <aside className="min-w-0 lg:sticky lg:top-4 lg:self-start">
            <div className="rounded-lg border border-grey-02 bg-white p-4">
              <label
                className="mb-2 block text-sm font-semibold text-grey-09"
                htmlFor="sanitize-review-run"
              >
                {t('pages.sanitize_review.run')}
              </label>

              {runsQuery.isLoading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-grey-06">
                  <Spinner
                    ariaLabel={t('pages.sanitize_review.loading_runs')}
                    className="h-5 w-5"
                  />
                  {t('pages.sanitize_review.loading_runs')}
                </div>
              ) : runsQuery.isError ? (
                <ErrorState
                  label={t('pages.sanitize_review.load_runs_error')}
                  onRetry={() => void runsQuery.refetch()}
                />
              ) : runs.length ? (
                <select
                  className="w-full rounded-lg border border-grey-03 bg-white px-3 py-2 text-sm text-grey-09 focus:border-primary-04 focus:outline-none focus:ring-2 focus:ring-primary-02"
                  id="sanitize-review-run"
                  onChange={(event) => changeRun(event.target.value)}
                  value={runId || ''}
                >
                  <option disabled value="">
                    {t('pages.sanitize_review.select_run')}
                  </option>
                  {runId && !selectedRun && (
                    <option value={runId}>{runId}</option>
                  )}
                  {runs.map((run) => (
                    <option key={run.id} value={run.id}>
                      {t('pages.sanitize_review.run_option', {
                        date: formatDate(run.createdAt, i18n.language),
                        processed: run.processedCount,
                        status: t(
                          `pages.sanitize_review.statuses.${normalizedStatus(
                            run.status
                          )}`,
                          { defaultValue: readableStatus(run.status) }
                        ),
                        total: run.totalCount,
                      })}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="rounded-lg bg-grey-01 p-3 text-sm text-grey-06">
                  {t('pages.sanitize_review.no_runs')}
                </p>
              )}

              {selectedRun && (
                <div className="mt-3">
                  <RunSummary run={selectedRun} />
                </div>
              )}

              {runId && (
                <>
                  <div className="my-4 border-t border-grey-02" />
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-grey-09">
                      {t('pages.sanitize_review.lessons')}
                    </h2>
                    {lessonsQuery.isFetching && !lessonsQuery.isLoading && (
                      <span className="text-xs text-grey-05">
                        {t('pages.sanitize_review.refreshing')}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
                    <label className="sr-only" htmlFor="sanitize-review-change">
                      {t('pages.sanitize_review.change_filter')}
                    </label>
                    <select
                      className="w-full rounded-lg border border-grey-03 bg-white px-3 py-2 text-sm text-grey-09"
                      id="sanitize-review-change"
                      onChange={(event) =>
                        changeLessonFilter(event.target.value, lessonStatus)
                      }
                      value={changedFilter}
                    >
                      <option value="all">
                        {t('pages.sanitize_review.all_changes')}
                      </option>
                      <option value="changed">
                        {t('pages.sanitize_review.changed_only')}
                      </option>
                      <option value="unchanged">
                        {t('pages.sanitize_review.unchanged_only')}
                      </option>
                    </select>

                    <label className="sr-only" htmlFor="sanitize-review-status">
                      {t('pages.sanitize_review.status_filter')}
                    </label>
                    <select
                      className="w-full rounded-lg border border-grey-03 bg-white px-3 py-2 text-sm text-grey-09"
                      id="sanitize-review-status"
                      onChange={(event) =>
                        changeLessonFilter(changedFilter, event.target.value)
                      }
                      value={lessonStatus}
                    >
                      <option value="">
                        {t('pages.sanitize_review.all_statuses')}
                      </option>
                      <option value="completed">
                        {t('pages.sanitize_review.statuses.completed')}
                      </option>
                      <option value="failed">
                        {t('pages.sanitize_review.statuses.failed')}
                      </option>
                    </select>
                  </div>

                  <div className="mt-3">
                    {lessonsQuery.isLoading ? (
                      <LoadingState
                        label={t('pages.sanitize_review.loading_lessons')}
                      />
                    ) : lessonsQuery.isError ? (
                      <ErrorState
                        label={t('pages.sanitize_review.load_lessons_error')}
                        onRetry={() => void lessonsQuery.refetch()}
                      />
                    ) : lessons.length ? (
                      <ul className="max-h-[48vh] space-y-2 overflow-y-auto pr-1 lg:max-h-[52vh]">
                        {lessons.map((lesson) => (
                          <LessonListItem
                            key={lesson.id}
                            lesson={lesson}
                            onSelect={() => setSelectedLesson(lesson.id)}
                            selected={lesson.id === selectedLessonId}
                          />
                        ))}
                      </ul>
                    ) : (
                      <p className="rounded-lg bg-grey-01 p-4 text-center text-sm text-grey-06">
                        {t('pages.sanitize_review.no_lessons')}
                      </p>
                    )}
                  </div>

                  {totalLessons > 0 && (
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <Button
                        disabled={offset === 0}
                        onClick={() => {
                          setSelectedLesson(null);
                          setOffset(Math.max(0, offset - LESSON_PAGE_SIZE));
                        }}
                        size="sm"
                        variant="outline"
                      >
                        {t('pages.sanitize_review.previous')}
                      </Button>
                      <span className="text-center text-xs text-grey-06">
                        {t('pages.sanitize_review.page_count', {
                          page,
                          pages: pageCount,
                        })}
                      </span>
                      <Button
                        disabled={offset + LESSON_PAGE_SIZE >= totalLessons}
                        onClick={() => {
                          setSelectedLesson(null);
                          setOffset(offset + LESSON_PAGE_SIZE);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        {t('pages.sanitize_review.next')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>

          <main className="min-w-0" id="sanitize-review-comparison">
            {!runId ? (
              <div className="flex min-h-[20rem] items-center justify-center rounded-lg border border-dashed border-grey-03 bg-grey-01 p-8 text-center text-sm text-grey-06">
                {t('pages.sanitize_review.select_run_prompt')}
              </div>
            ) : !selectedLesson ? (
              <div className="flex min-h-[20rem] items-center justify-center rounded-lg border border-dashed border-grey-03 bg-grey-01 p-8 text-center text-sm text-grey-06">
                {t('pages.sanitize_review.select_lesson_prompt')}
              </div>
            ) : !canLoadSelectedLesson ? (
              <div className="flex min-h-[20rem] flex-col items-center justify-center gap-3 rounded-lg border border-grey-02 bg-grey-01 p-8 text-center">
                <Tag
                  title={t(
                    `pages.sanitize_review.statuses.${normalizedStatus(
                      selectedLesson.status
                    )}`,
                    { defaultValue: readableStatus(selectedLesson.status) }
                  )}
                  variant={lessonTagVariant(selectedLesson)}
                />
                <p className="text-sm text-grey-06">
                  {t('pages.sanitize_review.content_unavailable')}
                </p>
                {selectedLesson.error && (
                  <div className="text-error-08 max-w-2xl rounded-md border border-error-02 bg-error-01 p-3 text-left text-sm">
                    {selectedLesson.error.code && (
                      <p className="font-mono font-semibold">
                        {selectedLesson.error.code}
                      </p>
                    )}
                    {selectedLesson.error.message && (
                      <p className="mt-1 break-words">
                        {selectedLesson.error.message}
                      </p>
                    )}
                    {selectedLesson.error.debug && (
                      <dl className="font-mono mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                        {selectedLesson.error.debug.stage && (
                          <>
                            <dt>stage</dt>
                            <dd>{selectedLesson.error.debug.stage}</dd>
                          </>
                        )}
                        {selectedLesson.error.debug.attemptCount && (
                          <>
                            <dt>attempts</dt>
                            <dd>{selectedLesson.error.debug.attemptCount}</dd>
                          </>
                        )}
                        {selectedLesson.error.debug.databaseCode && (
                          <>
                            <dt>database</dt>
                            <dd>{selectedLesson.error.debug.databaseCode}</dd>
                          </>
                        )}
                        {selectedLesson.error.debug.httpStatusCode && (
                          <>
                            <dt>http</dt>
                            <dd>{selectedLesson.error.debug.httpStatusCode}</dd>
                          </>
                        )}
                        {typeof selectedLesson.error.debug.retryable ===
                          'boolean' && (
                          <>
                            <dt>retryable</dt>
                            <dd>
                              {selectedLesson.error.debug.retryable
                                ? 'true'
                                : 'false'}
                            </dd>
                          </>
                        )}
                        {selectedLesson.error.debug.requestId && (
                          <>
                            <dt>request</dt>
                            <dd className="break-all">
                              {selectedLesson.error.debug.requestId}
                            </dd>
                          </>
                        )}
                      </dl>
                    )}
                  </div>
                )}
              </div>
            ) : contentQuery.isLoading ? (
              <LoadingState
                label={t('pages.sanitize_review.loading_content')}
              />
            ) : contentQuery.isError ? (
              <ErrorState
                label={t('pages.sanitize_review.load_content_error')}
                onRetry={() => void contentQuery.refetch()}
              />
            ) : contentQuery.data ? (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-grey-06">
                  <Tag
                    title={
                      selectedLesson.changed
                        ? t('pages.sanitize_review.changed')
                        : t('pages.sanitize_review.unchanged')
                    }
                    variant={lessonTagVariant(selectedLesson)}
                  />
                  <RiskScoreTag lesson={selectedLesson} />
                  <span>
                    {t('pages.sanitize_review.lesson_sizes', {
                      sanitized: formatBytes(selectedLesson.sanitizedSize),
                      source: formatBytes(selectedLesson.sourceSize),
                    })}
                  </span>
                </div>
                <LessonComparison
                  lessonId={selectedLesson.sourceLessonId}
                  lessonLabel={lessonLabel}
                  mode={viewMode}
                  onModeChange={setViewMode}
                  sanitizedHtml={contentQuery.data.sanitizedHtml}
                  sourceHtml={contentQuery.data.sourceHtml}
                />
              </>
            ) : null}
          </main>
        </div>
      </PageLayout.Content>
    </PageLayout>
  );
}

export default SanitizeReviewPage;
